import React from 'react';
import { Box, Typography, Button, Grid, Stack } from '@mui/material';
import { HomeownerPortalWrapper } from '../components/homeowner-portal-wrapper';
import warrantyPortal from '../../../assets/logos/warranty-portal.webp';
import bbbLogo from '../../../assets/logos/bbb-logo.png';
import { HeroSection } from '../components';
import { downloadFile } from '../../../helpers';
import axios from 'axios';
import { BASE_FILE_URL } from '../util';
import { BlueBackgroundWrapper } from '../components/blue-background-wrapper';
import { BuildsDropdown } from '../components';
import { WarrantyAskForm } from './warranty-ask-form';
import { ResidentialPerformanceGuide } from './residential-performance-guide';
import { WarrantyFaq } from './warranty-faq';

export const WarrantyPortal = () => {
  const [isDownloading, setIsDownloading] = React.useState({
    flyer: false,
    book: false,
    guide: false
  });
  const warrantyCoverage = [
    '1-Year Coverage - Covers workmanship and materials to ensure everything looks and works as it should.',
    '2-Year Coverage - Protects essential systems like HVAC, plumbing, electrical, and roofing.',
    `10-Year Structural Warranty - Safeguards the integrity of your home's foundation and framing.`
  ];

  return (
    <HomeownerPortalWrapper title="Learn More About My Home">
      <Box sx={{ flex: 1 }}>
        <HeroSection
          image={warrantyPortal}
          title="Warranty Portal"
          subtitle="All the information you need for your home"
          variant="small"
        />
        <Grid container>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', px: 4, pt: 2 }}>
            <BuildsDropdown sx={{ minWidth: { xs: '100%', md: 280 } }} />
          </Grid>
          <Grid item xs={12} lg={8}>
            <Box sx={{ py: { xs: 2, md: 4 }, pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 2 } }}>
              <Typography variant="h2">Your Home, Our Promise of Care</Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 2
                }}
              >
                We're proud to provide warranty protection that gives you peace of mind from day
                one.
              </Typography>

              <ul>
                {warrantyCoverage.map((milestone, index) => (
                  <Box component="li" key={index}>
                    <Typography variant="body1" component="span">
                      {milestone}
                    </Typography>
                  </Box>
                ))}
              </ul>
            </Box>

            <Box sx={{ pb: { xs: 4, md: 6 }, pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 2 } }}>
              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                <Grid item xs={12} sm={6} md={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      minHeight: '120px'
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{ mb: 1, whiteSpace: { xs: 'normal', md: 'nowrap' } }}
                    >
                      Warranty at a Glance
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, flex: 1 }}>
                      Looking for answers? This quick reference is a great place to start!
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        width: { xs: '100%', md: '275px' },
                        mt: 'auto'
                      }}
                      disabled={isDownloading.flyer ?? false}
                      onClick={async () => {
                        try {
                          setIsDownloading({
                            ...isDownloading,
                            flyer: true
                          });
                          const res = await axios.get(
                            `${BASE_FILE_URL}/Flyer_About%20Your%20Warranty_Maveric_Stone%20Martin.pdf`,
                            { responseType: 'blob' }
                          );
                          downloadFile(
                            res.data,
                            'Flyer_About Your Warranty_Maveric_Stone Martin.pdf'
                          );
                          // download pdf from storage
                        } catch (error) {
                          console.error('Error downloading warranty flyer:', error);
                        } finally {
                          setIsDownloading({
                            ...isDownloading,
                            flyer: false
                          });
                        }
                      }}
                    >
                      View Warranty Flyer
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      minHeight: { xs: '60px', md: '120px' }
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{ mb: 1, whiteSpace: { xs: 'normal', md: 'nowrap' } }}
                    >
                      Maverick Warranty
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: '1rem' }}></Box>
                    <Button
                      variant="contained"
                      sx={{
                        width: { xs: '100%', md: '275px' },
                        mt: 'auto'
                      }}
                      disabled={isDownloading.book ?? false}
                      onClick={async () => {
                        try {
                          setIsDownloading({
                            ...isDownloading,
                            book: true
                          });
                          const res = await axios.get(
                            `${BASE_FILE_URL}/250212_5_Warranty_Orientation_Packet.pdf`,
                            { responseType: 'blob' }
                          );
                          downloadFile(res.data, '250212_5_Warranty_Orientation_Packet.pdf');
                        } catch (error) {
                          console.error('Error downloading warranty packet:', error);
                        } finally {
                          setIsDownloading({
                            ...isDownloading,
                            book: false
                          });
                        }
                      }}
                    >
                      Download Warranty Book
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  minHeight: { xs: '100px', md: '450px' },
                  px: { xs: 0, md: 3 }
                }}
              >
                <Box
                  component="img"
                  src={bbbLogo}
                  alt="Better Business Bureau Logo"
                  sx={{
                    width: 'auto',
                    height: 'auto',
                    maxHeight: '525px',
                    maxWidth: '500px'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <BlueBackgroundWrapper>
          <Stack gap={1}>
            <Typography variant="h2" sx={{ mb: 1, whiteSpace: { xs: 'normal', md: 'nowrap' } }}>
              Your Homeowner Manual: Always Within Reach
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, flex: 1 }}>
              You'll receive a physical copy of your Homeowner Manual during your Homeowner
              Celebration—a special keepsake and go-to guide for everything in your new home, from
              warranty details to care tips and vendor contacts. And for easy access anytime, here’s
              the digital version—so whether you’re at home or on the go, the information you need
              is always right at your fingertips.
            </Typography>
          </Stack>
        </BlueBackgroundWrapper>
        <Stack gap={3} mt={2}>
          <WarrantyFaq />
          <ResidentialPerformanceGuide />
          <WarrantyAskForm />
        </Stack>
      </Box>
    </HomeownerPortalWrapper>
  );
};
